import { HttpMethod } from '@previo/core'
import type { ObjectKindId, ServiceId } from '@previo/codebooks'
import { APIPaths } from '~/http/APIPaths'
import Http from '~/http/Http'
import type {
  PropertyBankAccountsResponse,
  PropertyDetailResponse,
  PropertyGuestCategoriesResponse,
  PropertyListResponse,
  PropertyMarketCodesResponse,
  PropertyModules,
  PropertyObjectKindsResponse,
  PropertySalesman,
  PropertyServiceAccountingResponse,
  PropertyServicesResponse,
  PropertyStayPackagesResponse,
  PropertyTerminalsResponse,
} from '~/http/property/types'

export class PropertyAPI extends Http {
  services = (): Promise<PropertyServicesResponse> => {
    return this.callAuthWithPropertyAPI<null, PropertyServicesResponse>(
      HttpMethod.GET,
      {
        path: APIPaths.PROPERTY_SERVICES_LIST,
      },
      null,
    )
  }

  serviceAccounting = (serviceId: ServiceId): Promise<PropertyServiceAccountingResponse> => {
    return this.callAuthWithPropertyAPI<null, PropertyServiceAccountingResponse>(HttpMethod.GET, {
      path: APIPaths.PROPERTY_SERVICE_ACCOUNTING,
      params: { serviceId },
    })
  }

  servicesByObjectKinds = (objectKindIds: number[]): Promise<PropertyServicesResponse> => {
    return this.callAuthWithPropertyAPI<null, PropertyServicesResponse>(
      HttpMethod.GET,
      {
        path: APIPaths.PROPERTY_SERVICES_BY_OBJECT_KINDS_LIST,
        query: {
          objectKindIds,
        },
      },
      null,
    )
  }

  stayPackages = (): Promise<PropertyStayPackagesResponse> => {
    return this.callAuthWithPropertyAPI<null, PropertyStayPackagesResponse>(
      HttpMethod.GET,
      {
        path: APIPaths.PROPERTY_STAY_PACKAGES_LIST,
      },
      null,
    )
  }

  stayPackagesSuggestions = (
    date: string,
    objectKindIds: ObjectKindId[] = [],
  ): Promise<PropertyStayPackagesResponse> => {
    return this.callAuthWithPropertyAPI<null, PropertyStayPackagesResponse>(
      HttpMethod.GET,
      {
        path: APIPaths.PROPERTY_STAY_PACKAGES_SUGGESTIONS,
        query: { date, objectKindIds },
      },
      null,
    )
  }

  objectKinds = (): Promise<PropertyObjectKindsResponse> => {
    return this.callAuthWithPropertyAPI<null, PropertyObjectKindsResponse>(
      HttpMethod.GET,
      {
        path: APIPaths.PROPERTY_OBJECT_KINDS_LIST,
      },
      null,
    )
  }

  marketCodes = (): Promise<PropertyMarketCodesResponse> => {
    return this.callAuthWithPropertyAPI<object, PropertyMarketCodesResponse>(
      HttpMethod.GET,
      {
        path: APIPaths.PROPERTY_MARKET_CODES_LIST,
      },
      null,
    )
  }

  loadProperties = (): Promise<PropertyListResponse> => {
    return this.callAuthAPI<null, PropertyListResponse>(HttpMethod.GET, {
      path: APIPaths.PROPERTY_PROPERTIES_LIST,
    })
  }

  loadProperty = (propertyId: number): Promise<PropertyDetailResponse> => {
    return this.callAuthAPI<null, PropertyDetailResponse>(HttpMethod.GET, {
      path: APIPaths.PROPERTY_PROPERTY_DETAIL,
      params: {
        propertyId: propertyId,
      },
    })
  }

  guestCategories = (): Promise<PropertyGuestCategoriesResponse> => {
    return this.callAuthWithPropertyAPI<null, PropertyGuestCategoriesResponse>(HttpMethod.GET, {
      path: APIPaths.PROPERTY_GUEST_CATEGORIES_LIST,
    })
  }

  loadModules = (): Promise<PropertyModules> => {
    return this.callAuthWithPropertyAPI<null, PropertyModules>(HttpMethod.GET, {
      path: APIPaths.PROPERTY_SETTINGS_MODULES,
    })
  }

  loadSalesman = (): Promise<PropertySalesman> => {
    return this.callAuthWithPropertyAPI<null, PropertySalesman>(HttpMethod.GET, {
      path: APIPaths.PROPERTY_SALESMAN_GET,
    })
  }

  bankAccounts = (): Promise<PropertyBankAccountsResponse> => {
    return this.callAuthWithPropertyAPI<null, PropertyBankAccountsResponse>(HttpMethod.GET, {
      path: APIPaths.PROPERTY_BANK_ACCOUNTS,
    })
  }

  activeTerminals = (): Promise<PropertyTerminalsResponse> => {
    return this.callAuthWithPropertyAPI<null, PropertyTerminalsResponse>(HttpMethod.GET, {
      path: APIPaths.PROPERTY_TERMINALS_ACTIVE,
    })
  }
}

export const usePropertyAPI = (): PropertyAPI => {
  return new PropertyAPI()
}
